import {
  TIPOS_ITEM_COBRANCA_HORA_DIFERENCIADOS,
  TipoItemCobrancaHoraEnum,
} from 'src/app/enums/tipoItemCobrancaHora.enum';
import { IConsumoContratoItem } from 'src/app/interfaces/consumo-contrato.interface';
import { TipoModalidadeHorasContrato } from 'src/app/interfaces/consumo-contratos-cliente.interface';

const TIPOS_CONSUMO_HORAS: Record<number, string> = {
  [TipoItemCobrancaHoraEnum.SemDiferenciacao]: 'CONSUMO_CONTRATO_SEM_DIFERENCIACAO',
  [TipoItemCobrancaHoraEnum.InternoOuExterno]: 'CONSUMO_CONTRATO_INTERNO_OU_EXTERNO',
  [TipoItemCobrancaHoraEnum.InternoOuExternoForaExpediente]: 'CONSUMO_CONTRATO_INTERNO_OU_EXTERNO_FORA_EXPEDIENTE',
  [TipoItemCobrancaHoraEnum.Interno]: 'CONSUMO_CONTRATO_INTERNO',
  [TipoItemCobrancaHoraEnum.InternoForaExpediente]: 'CONSUMO_CONTRATO_INTERNO_FORA_EXPEDIENTE',
  [TipoItemCobrancaHoraEnum.Externo]: 'CONSUMO_CONTRATO_EXTERNO',
  [TipoItemCobrancaHoraEnum.ExternoForaExpediente]: 'CONSUMO_CONTRATO_EXTERNO_FORA_EXPEDIENTE',
};

// "Sem Diferenciacao" (36) é a primeira modalidade de horas, seguida das seis diferenciadas
export const ORDEM_TIPOS_CONSUMO_HORAS: number[] = [
  TipoItemCobrancaHoraEnum.SemDiferenciacao,
  ...TIPOS_ITEM_COBRANCA_HORA_DIFERENCIADOS,
];

export interface IModalidadeConsumoContrato {
  tipoConsumo: 'tickets' | 'horas';
  tipoItemCobrancaId: number | null;
  item: IConsumoContratoItem | null;
  configurada: boolean;
}

export function montarModalidadesConsumo(consumos: IConsumoContratoItem[]): IModalidadeConsumoContrato[] {
  const modalidadesTickets: IModalidadeConsumoContrato[] = consumos
    .filter((item) => item.tipo_consumo === 'tickets')
    .map((item) => ({
      tipoConsumo: 'tickets' as const,
      tipoItemCobrancaId: item.tipo_item_cobranca_id,
      item,
      configurada: true,
    }));
  const consumosHoras = consumos.filter((item) => item.tipo_consumo === 'horas');

  if (!consumosHoras.length) {
    return modalidadesTickets;
  }

  const cobrancasHoras = Array.from(new Set(consumosHoras.map((item) => item.cobranca_id)));
  const modalidadesHoras = cobrancasHoras.reduce<IModalidadeConsumoContrato[]>((modalidades, cobrancaId) => {
    const itensCobranca = consumosHoras.filter((item) => item.cobranca_id === cobrancaId);
    const semDiferenciacao = itensCobranca.find(
      (consumo) => consumo.tipo_item_cobranca_id === TipoItemCobrancaHoraEnum.SemDiferenciacao
    ) || null;

    // a configuração absoluta e as modalidades diferenciadas nunca coexistem: quando o item 36
    // está configurado, exibimos apenas a barra dele, sem listar as seis como não configuradas
    const tiposExibidos: number[] = semDiferenciacao
      ? [TipoItemCobrancaHoraEnum.SemDiferenciacao]
      : TIPOS_ITEM_COBRANCA_HORA_DIFERENCIADOS;

    tiposExibidos.forEach((tipoItemCobrancaId) => {
      const item = itensCobranca.find((consumo) => consumo.tipo_item_cobranca_id === tipoItemCobrancaId) || null;
      modalidades.push({
        tipoConsumo: 'horas',
        tipoItemCobrancaId,
        item,
        configurada: !!item,
      });
    });

    // preserva itens de horas devolvidos pela API fora da ordem conhecida, para não ocultar consumo existente
    itensCobranca
      .filter((consumo) =>
        consumo.tipo_item_cobranca_id === null ||
        !ORDEM_TIPOS_CONSUMO_HORAS.includes(consumo.tipo_item_cobranca_id)
      )
      .forEach((consumo) => {
        modalidades.push({
          tipoConsumo: 'horas',
          tipoItemCobrancaId: consumo.tipo_item_cobranca_id,
          item: consumo,
          configurada: true,
        });
      });

    return modalidades;
  }, []);

  return modalidadesTickets.concat(modalidadesHoras);
}

export function chaveTraducaoModalidadeConsumo(modalidade: IModalidadeConsumoContrato): string {
  if (modalidade.tipoConsumo === 'tickets') {
    return 'CONSUMO_CONTRATO_TICKETS';
  }

  return modalidade.tipoItemCobrancaId !== null
    ? TIPOS_CONSUMO_HORAS[modalidade.tipoItemCobrancaId] || 'CONSUMO_CONTRATO_HORAS'
    : 'CONSUMO_CONTRATO_HORAS';
}

export function larguraBarraConsumo(percentual: number | null | undefined): number {
  if (percentual === null || percentual === undefined || !Number.isFinite(percentual)) {
    return 0;
  }

  return Math.min(Math.max(percentual, 0), 100);
}

export function classeBarraConsumo(percentual: number | null | undefined): string {
  if (percentual === null || percentual === undefined || !Number.isFinite(percentual)) {
    return 'bg-info';
  }

  if (percentual < 24) {
    return 'bg-success';
  }

  if (percentual < 49) {
    return 'bg-info';
  }

  if (percentual < 74) {
    return 'bg-warning';
  }

  return 'bg-danger';
}

/**
 * Formata percentual de consumo protegendo a interface contra valores inválidos
 * vindos da API (NaN, Infinity). `null`/`undefined` é tratado à parte pelos
 * chamadores (significa "sem limite"). Não recalcula nem limita o percentual —
 * apenas arredonda casas decimais excessivas; um consumo acima de 100% (ex.:
 * excedente) deve continuar sendo exibido como tal. Só a barra visual
 * (`larguraBarraConsumo`) é limitada a 100%.
 */
export function formatarPercentualConsumo(percentual: number | null | undefined): string {
  if (percentual === null || percentual === undefined || !Number.isFinite(percentual)) {
    return '--';
  }

  const arredondado = Math.round(percentual * 10) / 10;

  return `${arredondado}%`;
}

export function chaveTraducaoTipoConsumo(item: IConsumoContratoItem): string {
  if (item.tipo_consumo === 'tickets') {
    return 'CONSUMO_CONTRATO_TICKETS';
  }

  return item.tipo_item_cobranca_id !== null
    ? TIPOS_CONSUMO_HORAS[item.tipo_item_cobranca_id] || 'CONSUMO_CONTRATO_HORAS'
    : 'CONSUMO_CONTRATO_HORAS';
}

/** Colunas da tabela "Franquia de Horas": Categorias + as 7 modalidades, nesta ordem. */
export const MODALIDADES_HORAS: { tipo: TipoModalidadeHorasContrato; chave: string; chaveBase: string; foraExpediente: boolean }[] = [
  { tipo: 'sem_diferenciacao', chave: 'HORA_SEM_DIFERENCIACAO', chaveBase: 'HORA_SEM_DIFERENCIACAO', foraExpediente: false },
  { tipo: 'interno_ou_externo', chave: 'HORA_INTERNO_OU_EXTERNO', chaveBase: 'HORA_INTERNO_OU_EXTERNO', foraExpediente: false },
  { tipo: 'interno_ou_externo_fora', chave: 'HORA_INTERNO_OU_EXTERNO_FORA_EXPEDIENTE', chaveBase: 'HORA_INTERNO_OU_EXTERNO', foraExpediente: true },
  { tipo: 'interno', chave: 'HORA_INTERNO', chaveBase: 'HORA_INTERNO', foraExpediente: false },
  { tipo: 'interno_fora', chave: 'HORA_INTERNO_FORA_EXPEDIENTE', chaveBase: 'HORA_INTERNO', foraExpediente: true },
  { tipo: 'externo', chave: 'HORA_EXTERNO', chaveBase: 'HORA_EXTERNO', foraExpediente: false },
  { tipo: 'externo_fora', chave: 'HORA_EXTERNO_FORA_EXPEDIENTE', chaveBase: 'HORA_EXTERNO', foraExpediente: true },
];

export interface ILinhaHorasTickets {
  nome: string;
  /** Árvore completa da categoria (ex.: "Fruta/Banana"); `null` quando igual ao nome. */
  arvore: string | null;
  /** Parte da árvore antes do nome (ex.: "Fruta/"), exibida sem negrito. */
  prefixoArvore: string;
  horas: { [tipo: string]: any };
  ticket: any;
}

/** Linha de uma categoria (nome em negrito + árvore) a partir do contrato devolvido por `/contratos/consumo`. */
export function montarLinhaCategoriaConsumo(categoria, nomeTodasCategorias: string): ILinhaHorasTickets {
  const todas = categoria.categoria_todas || !categoria.categoria_nome;
  const nome = todas ? nomeTodasCategorias : categoria.categoria_nome;
  const arvore = !todas && categoria.categoria_arvore && categoria.categoria_arvore !== nome ? categoria.categoria_arvore : null;
  return {
    nome,
    arvore,
    prefixoArvore: arvore && arvore.endsWith(nome) ? arvore.slice(0, arvore.length - nome.length) : '',
    horas: {},
    ticket: null
  };
}

/** Agrupa as categorias em linhas (uma por categoria) e ordena alfanumericamente pelo nome exibido. */
export function montarLinhasConsumo(
  categorias,
  nomeTodasCategorias: string,
  preencher: (linha: ILinhaHorasTickets, categoria) => void
): ILinhaHorasTickets[] {
  const linhas: ILinhaHorasTickets[] = [];
  const indices = new Map<string, ILinhaHorasTickets>();
  categorias.forEach(categoria => {
    const chave = categoria.categoria_todas || categoria.categoria_id === null ? 'todas' : String(categoria.categoria_id);
    if (!indices.has(chave)) {
      const linha = montarLinhaCategoriaConsumo(categoria, nomeTodasCategorias);
      indices.set(chave, linha);
      linhas.push(linha);
    }
    preencher(indices.get(chave), categoria);
  });
  return linhas.sort((a, b) => a.nome.localeCompare(b.nome, undefined, { sensitivity: 'base', numeric: true }));
}

/**
 * Item de consumo ilimitado (MIL-30081): quando a API envia `ilimitado` (boolean), só ele vale — `franquia: null`
 * com `ilimitado: false` é consumo excedido, não ∞. Sem `ilimitado` (API antiga), `franquia`/`franquia_min` nulos
 * indicam ilimitado. Horas das modais usam `franquia_min`.
 */
export function consumoItemIlimitado(item): boolean {
  if (!item) {
    return false;
  }
  if (typeof item.ilimitado === 'boolean') {
    return item.ilimitado;
  }
  return item.franquia === null || item.franquia_min === null;
}

/** Tooltip da célula de consumo (modais de contrato): "ilimitado" ou "consumido (percentual)". */
export function tituloConsumoContrato(consumido: string | number, item, textoIlimitado: string): string {
  if (consumoItemIlimitado(item)) {
    return textoIlimitado;
  }
  // franquia zerada já consumida: não há percentual calculável
  const franquiaZeroExcedida = !!item.excedido && (item.franquia_min === 0 || item.franquia === 0);
  return franquiaZeroExcedida ? String(consumido) : `${consumido} (${formatarPercentualConsumo(item.percentual)})`;
}
